import './App.css';
import './GameCards.css';
import { useState, useEffect, useContext } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ToastProvider } from './ToastContext';
import { WalletProvider, WalletContext } from './WalletContext';
import Toast from './Toast';
import NotFound from './NotFound';
import NewsHeadline from './NewsHeadline';
import Login from './Login';
import Register from './Register';
import Funds from './Funds';
import DepositHistory from './DepositHistory';
import MyBids from './MyBids';
import NormalBidHistory from './NormalBidHistory';
import StarlineBidHistory from './StarlineBidHistory';
import GaliDesawar from './GaliDesawar';
import Starline from './Starline';
import AddFunds from './AddFunds';
import WithdrawFunds from './WithdrawFunds';
import WithdrawalHistory from './WithdrawalHistory';
import ContactUs from './ContactUs';
import BankDetails from './BankDetails';
import GameRates from './GameRates';
import GameSelection from './GameSelection';
import StarlineGameSelection from './StarlineGameSelection';
import ResultChart from './ResultChart';
import GameEntry from './GameEntry';
import Passbook from './Passbook';
import SingleDigitGame from './SingleDigitGame';
import JodiGame from './JodiGame';
import SinglePannaGame from './SinglePannaGame';
import DoublePannaGame from './DoublePannaGame';
import TriplePannaGame from './TriplePannaGame';
import HalfSangamGame from './HalfSangamGame';
import FullSangamGame from './FullSangamGame';
import StarlineSingleDigitGame from './StarlineSingleDigitGame';
import StarlineSinglePannaGame from './StarlineSinglePannaGame';
import StarlineDoublePannaGame from './StarlineDoublePannaGame';
import StarlineTriplePannaGame from './StarlineTriplePannaGame';

// Helper function to check if a route is valid (not 404)
function isValidRoute(pathname) {
  // List of all valid route patterns
  const validRoutes = [
    '/login',
    '/register',
    '/',
    '/starline',
    '/galidesawar',
    '/mybids',
    '/normal-bid-history',
    '/starline-bid-history',
    '/passbook',
    '/funds',
    '/addfunds',
    '/withdrawfunds',
    '/withdrawal-history',
    '/deposit-history',
    '/contactus',
    '/bankdetails',
    '/gamerates'
  ];

  // Check exact matches
  if (validRoutes.includes(pathname)) {
    return true;
  }

  // Check dynamic routes with patterns
  const dynamicPatterns = [
    /^\/starline-game-selection\/\d+$/,
    /^\/game-selection\/\d+$/,
    /^\/single-digit-game\/\d+$/,
    /^\/jodi-game\/\d+$/,
    /^\/single-panna-game\/\d+$/,
    /^\/double-panna-game\/\d+$/,
    /^\/triple-panna-game\/\d+$/,
    /^\/half-sangam-game\/\d+$/,
    /^\/full-sangam-game\/\d+$/,
    /^\/starline-single-digit-game\/\d+$/,
    /^\/starline-single-panna-game\/\d+$/,
    /^\/starline-double-panna-game\/\d+$/,
    /^\/starline-triple-panna-game\/\d+$/,
    /^\/result-chart\/\d+$/
  ];

  return dynamicPatterns.some(pattern => pattern.test(pathname));
}

function AppContent() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger re-render for status updates
  const [showNotifications, setShowNotifications] = useState(false);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { walletBalance, updateWalletBalance } = useContext(WalletContext);

  useEffect(() => {
    if (authChecked) return; // only run once at startup

    const storedUser = localStorage.getItem('user');
    const isAuthRoute = location.pathname === '/login' || location.pathname === '/register';

    if (isAuthRoute) {
      setAuthChecked(true);
      return;
    }

    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        if (parsedUser?.id) {
          updateWalletBalance(parsedUser.id);

          fetch(`https://backend.mainbazar777.site/api/fetch-user/${parsedUser.id}`)
            .then(res => res.json())
            .then(data => {
              if (data.success && data.user?.admin_approved == 1) {
                // Admin just approved — update localStorage and proceed
                localStorage.setItem('user', JSON.stringify({ ...parsedUser, admin_approved: 1 }));
                setUser(prev => ({ ...prev, admin_approved: 1 }));
                setAuthChecked(true);
              } else if (data.success && data.user?.admin_approved == 0) {
                window.location.href = 'https://dhanlaxmi365.xyz/v1/';
              } else {
                setAuthChecked(true);
              }
            })
            .catch(err => {
              console.error('Error fetching user:', err);
              setAuthChecked(true);
            });

        } else {
          setAuthChecked(true);
        }
      } catch (err) {
        console.error('Error parsing user data:', err);
        setAuthChecked(true);
      }
    } else {
      setAuthChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Route-change auth gate: no geo check, no async work — just sync user state
  // from localStorage (post-login) and redirect unauthenticated users.
  useEffect(() => {
    if (!authChecked) return;
    const storedUser = localStorage.getItem('user');
    const isAuthRoute = location.pathname === '/login' || location.pathname === '/register';

    if (!storedUser) {
      if (!isAuthRoute) navigate('/register');
      return;
    }

    if (!user) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        if (parsedUser?.id) {
          updateWalletBalance(parsedUser.id);
        }
      } catch (err) {
        console.error('Error parsing user data:', err);
        if (!isAuthRoute) navigate('/register');
      }
    }
  }, [authChecked, location.pathname, navigate, updateWalletBalance, user]);

  // Fetch news headline data
  useEffect(() => {
    const fetchNewsData = async () => {
      try {
        const response = await fetch('https://backend.mainbazar777.site/api/news-headline');
        if (response.ok) {
          const data = await response.json();
          if (data) {
            setNewsData({
              contact_number: data.contact_number || '',
              whatsapp_url: data.whatsapp_url || '',
              news_headline: data.news_headline || '',
              telegram_url: data.telegram_url || ''
            });
          }
        }
      } catch (err) {
        console.error('Error fetching news data:', err);
      }
    };

    fetchNewsData();
  }, []);

  // Fetch notifications from API
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          const userId = parsedUser?.id;

          if (userId) {
            const response = await fetch('https://backend.mainbazar777.site/api/notifications', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ user_id: userId })
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.data) {
                // Transform API data to match UI format
                const formattedNotifications = data.data.map(notif => ({
                  id: notif.id,
                  title: notif.title,
                  message: notif.message,
                  type: notif.type,
                  is_read: notif.is_read,
                  time: new Date(notif.created_at).toLocaleString()
                }));
                setNotifications(formattedNotifications);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching notifications:', err);
      }
    };

    // Fetch notifications on component mount
    fetchNotifications();

    // Set interval to refresh notifications every 30 seconds
    const intervalId = setInterval(fetchNotifications, 30000);

    return () => clearInterval(intervalId);
  }, []);

  // Disable touch zoom functionality on mobile devices
  useEffect(() => {
    // Prevent pinch zoom
    const handleTouchMove = (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    // Prevent double-tap zoom
    let lastTouchEnd = 0;
    const handleTouchEnd = (e) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, false);

    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);

  const [notifications, setNotifications] = useState([]);
  const [newsData, setNewsData] = useState({
    contact_number: '8000673615',
    whatsapp_url: 'https://wa.me/918000673615',
    news_headline: '',
    telegram_url: 'https://t.me/kalyanlineofficial'
  });

  // Format 8-digit result as XXX-XX-XXX with asterisks for missing digits
  const formatGameResult = (result) => {
    if (!result) {
      return '***-**-***';
    }

    // Remove any existing dashes or spaces
    const cleanResult = result.toString().replace(/[-\s]/g, '');

    // If it's already the placeholder, return it
    if (cleanResult.includes('*')) {
      return result;
    }

    // Get only digits
    const digits = cleanResult.replace(/[^0-9]/g, '');
    const length = digits.length;

    if (length === 0) {
      return '***-**-***';
    } else if (length <= 3) {
      // Show first 1-3 digits, rest as asterisks
      const part1 = digits.padEnd(3, '*');
      return `${part1}-**-***`;
    } else if (length === 4) {
      // Show first 3 digits and 4th digit, rest as asterisks
      const part1 = digits.substring(0, 3);
      const part2 = digits.substring(3, 4) + '*';
      return `${part1}-${part2}-***`;
    } else if (length <= 5) {
      // Show first 3 and next 2 digits, rest as asterisks
      const part1 = digits.substring(0, 3);
      const part2 = digits.substring(3, 5).padEnd(2, '*');
      return `${part1}-${part2}-***`;
    } else if (length <= 8) {
      // Show all available digits up to 8
      const part1 = digits.substring(0, 3);
      const part2 = digits.substring(3, 5);
      const part3 = digits.substring(5, 8).padEnd(3, '*');
      return `${part1}-${part2}-${part3}`;
    } else {
      // More than 8 digits, take only first 8
      const part1 = digits.substring(0, 3);
      const part2 = digits.substring(3, 5);
      const part3 = digits.substring(5, 8);
      return `${part1}-${part2}-${part3}`;
    }
  };

  // Scroll to top when route changes
  useEffect(() => {
    // Scroll the main content container to top
    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
    // Also scroll window for good measure
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // Fetch normal games
  useEffect(() => {
    const fetchNormalGames = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://backend.mainbazar777.site/api/fetch-normal-game');

        if (!response.ok) {
          throw new Error('Failed to fetch games');
        }

        const data = await response.json();

        if (data.success && data.normal_games) {
          // Filter games where status = 1 and then format them
          const formattedGames = data.normal_games
            .filter(game => game.status === 1)
            .map(game => ({
              id: game.id,
              name: game.game_name,
              numbers: formatGameResult(game.results),
              status: game.status === 1 ? 'Market Running' : 'Market Close',
              openTime: formatTime(game.open),
              closeTime: formatTime(game.close),
              rawOpen: game.open,
              rawClose: game.close,
              gameClosed: game.closed == 1
            }));
          setGames(formattedGames);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching normal games:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    // Fetch on component mount
    fetchNormalGames();

    // Set interval to fetch every 5 minutes (300000 milliseconds)
    const intervalId = setInterval(fetchNormalGames, 300000);

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  // Smart Timer: Calculate next status change and set precise timer
  useEffect(() => {
    if (games.length === 0) return;

    const calculateNextStatusChange = () => {
      const now = new Date();
      const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
      const currentTimeInMinutes = kolkataTime.getHours() * 60 + kolkataTime.getMinutes();

      let nearestChangeInMinutes = null;

      games.forEach(game => {
        if (!game.rawOpen || !game.rawClose) return;

        const [openHours, openMinutes] = game.rawOpen.split(':').map(num => parseInt(num));
        const openTimeInMinutes = openHours * 60 + openMinutes;

        const [closeHours, closeMinutes] = game.rawClose.split(':').map(num => parseInt(num));
        const closeTimeInMinutes = closeHours * 60 + closeMinutes;

        // Calculate minutes until opening
        let minutesToOpen;
        if (openTimeInMinutes > currentTimeInMinutes) {
          minutesToOpen = openTimeInMinutes - currentTimeInMinutes;
        } else {
          minutesToOpen = (24 * 60) - currentTimeInMinutes + openTimeInMinutes; // Next day
        }

        // Calculate minutes until closing
        let minutesToClose;
        if (closeTimeInMinutes > currentTimeInMinutes) {
          minutesToClose = closeTimeInMinutes - currentTimeInMinutes;
        } else {
          minutesToClose = (24 * 60) - currentTimeInMinutes + closeTimeInMinutes; // Next day
        }

        // Find the nearest change (either open or close)
        const nextChange = Math.min(minutesToOpen, minutesToClose);

        if (nearestChangeInMinutes === null || nextChange < nearestChangeInMinutes) {
          nearestChangeInMinutes = nextChange;
        }
      });

      return nearestChangeInMinutes;
    };

    const nextChangeInMinutes = calculateNextStatusChange();

    if (nextChangeInMinutes !== null && nextChangeInMinutes > 0) {
      // Set timer for exact moment of next status change
      const timeoutId = setTimeout(() => {
        setRefreshTrigger(prev => prev + 1); // Trigger re-render
      }, nextChangeInMinutes * 60 * 1000); // Convert minutes to milliseconds

      return () => clearTimeout(timeoutId); // Cleanup
    }
  }, [games, refreshTrigger]);

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  };

  // Get market status based on Asia/Kolkata time.
  // Market day resets at 2:00 AM, so times between 12:00 AM and 2:00 AM belong to the
  // previous day's market window. Markets close 5 minutes before their listed close time.
  const getMarketStatus = (openTime, closeTime, gameClosed) => {
    // If gameClosed is 1, always return closed
    if (gameClosed === true || gameClosed === 1) {
      return 'closed';
    }

    if (!openTime || !closeTime) return 'closed';

    const now = new Date();
    const kolkataTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const currentHours = kolkataTime.getHours();
    const currentMinutes = kolkataTime.getMinutes();

    // Convert clock time to "market day minutes" where 0 = 2:00 AM reset point.
    // This makes midnight (00:00) = 1320 and 1:59 AM = 1439, keeping them in the
    // same market day as the preceding evening rather than the next morning.
    const toMarketMinutes = (h, m) => ((h * 60 + m) - 120 + 1440) % 1440;

    const currentMarketMinutes = toMarketMinutes(currentHours, currentMinutes);

    const [closeHours, closeMinutesVal] = closeTime.split(':').map(Number);
    const closeMarketMinutes = toMarketMinutes(closeHours, closeMinutesVal);

    // Market closes 5 minutes before the listed close time
    if (currentMarketMinutes >= closeMarketMinutes - 5) {
      return 'closed';
    }

    return 'running';
  };

  // Check if market is open
  const isMarketOpen = (openTime, closeTime, gameClosed) => {
    return getMarketStatus(openTime, closeTime, gameClosed) === 'running';
  };

  // Get display text for market status
  const getMarketStatusText = (openTime, closeTime, gameClosed) => {
    const status = getMarketStatus(openTime, closeTime, gameClosed);
    switch (status) {
      case 'running':
        return 'Market Running';
      case 'yet-to-open':
        return 'Market Yet to Open';
      case 'closed':
        return 'Market Closed';
      default:
        return 'Market Closed';
    }
  };

  const handlePlayClick = (game, event) => {
    if (isMarketOpen(game.rawOpen, game.rawClose, game.gameClosed)) {
      navigate(`/game-selection/${game.id}`, {
        state: {
          gameName: game.name,
          openTime: game.rawOpen,
          closeTime: game.rawClose
        }
      });
    } else {
      // Add wobble animation to card
      const cardElement = event.currentTarget.closest('.gcard-item');
      if (cardElement) {
        cardElement.classList.add('wobble');
        setTimeout(() => {
          cardElement.classList.remove('wobble');
        }, 500); // Match the animation duration
      }

      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      if (navigator.vibrate) {
        navigator.vibrate(200);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setIsSidebarOpen(false);
    navigate('/login');
  };

  // Mark notifications as read
  const markNotificationsAsRead = async () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const userId = parsedUser?.id;

        if (userId && notifications.length > 0) {
          // Get only unread notification IDs
          const unreadNotificationIds = notifications
            .filter(notif => notif.is_read === 0)
            .map(notif => notif.id);

          if (unreadNotificationIds.length > 0) {
            const response = await fetch('https://backend.mainbazar777.site/api/notifications-read', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                user_id: userId,
                notification_ids: unreadNotificationIds
              })
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                // Update local notifications to mark as read
                const updatedNotifications = notifications.map(notif => ({
                  ...notif,
                  is_read: 1
                }));
                setNotifications(updatedNotifications);
              }
            }
          }
        }
      }
    } catch (err) {
      console.error('Error marking notifications as read:', err);
    }
  };

  const menuItems = [
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="#d4af37">
          <path d="M14 4L4 11V24H10V18H18V24H24V11L14 4Z" />
        </svg>
      ),
      title: 'Home',
      subtitle: 'Your Main Screen'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="#d4af37">
          <rect x="6" y="8" width="16" height="12" rx="2" stroke="#d4af37" strokeWidth="2" fill="none" />
          <path d="M6 12H22M10 8V12M18 8V12" stroke="#d4af37" strokeWidth="2" />
        </svg>
      ),
      title: 'PassBook',
      subtitle: 'Your All History'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <rect x="8" y="10" width="12" height="10" stroke="#d4af37" strokeWidth="2" />
          <path d="M8 14H20M11 10V8M17 10V8M14 14V20" stroke="#d4af37" strokeWidth="2" />
        </svg>
      ),
      title: 'Bank Details',
      subtitle: 'Add Bank Details'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <circle cx="14" cy="14" r="10" stroke="#d4af37" strokeWidth="2" />
          <path d="M14 9V15L18 17" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
      title: 'Bid History',
      subtitle: 'Bid History'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M8 18V12C8 10 9 8 11 8C11 8 11 6 14 6C17 6 17 8 17 8C19 8 20 10 20 12V18" stroke="#d4af37" strokeWidth="2" />
          <path d="M14 12V22M10 16H18" stroke="#d4af37" strokeWidth="2" />
        </svg>
      ),
      title: 'Game Rate',
      subtitle: 'Game Rate'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M8 8L14 4L20 8L20 18L14 22L8 18Z" stroke="#d4af37" strokeWidth="2" />
          <circle cx="14" cy="13" r="3" stroke="#d4af37" strokeWidth="2" />
        </svg>
      ),
      title: 'Contact Us',
      subtitle: 'Contact Us'
    },
    {
      icon: (
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M7 20V8C7 6.9 7.9 6 9 6H19C20.1 6 21 6.9 21 8V20M7 20H21M7 20L5 22M21 20L23 22M14 9V17M10 13H18" stroke="#d32f2f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ),
      title: 'Logout',
      subtitle: 'Sign out'
    }
  ];

  // Check if on login or register route
  const isAuthRoute = location.pathname === '/login' || location.pathname === '/register';

  if (!authChecked) {
    return null;
  }

  return (
    <div className="App">
      {/* Sidebar Overlay */}
      {!isAuthRoute && isValidRoute(location.pathname) && (
        <div
          className={`sidebar-overlay ${isSidebarOpen ? 'active' : ''}`}
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      {!isAuthRoute && isValidRoute(location.pathname) && (
        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="profile-section">
              <div className="profile-avatar">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                  <circle cx="30" cy="30" r="30" fill="#e8eef5" />
                  <circle cx="30" cy="22" r="10" fill="#d4af37" />
                  <path d="M10 50C10 38 18 32 30 32C42 32 50 38 50 50" fill="#d4af37" />
                </svg>
                <span className="online-indicator"></span>
              </div>
              <div className="profile-info">
                <h2>{user?.name || user?.fullname || 'User'}</h2>
                <p>{user?.contact_number || user?.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          <nav className="sidebar-menu">
            {menuItems.map((item, index) => (
              <button
                key={index}
                className="menu-item"
                onClick={() => {
                  if (item.title === 'Home') {
                    navigate('/');
                    setIsSidebarOpen(false);
                  } else if (item.title === 'PassBook') {
                    navigate('/passbook');
                    setIsSidebarOpen(false);
                  } else if (item.title === 'Game Rate') {
                    navigate('/gamerates');
                    setIsSidebarOpen(false);
                  } else if (item.title === 'Contact Us') {
                    navigate('/contactus');
                    setIsSidebarOpen(false);
                  } else if (item.title === 'Bank Details') {
                    navigate('/bankdetails');
                    setIsSidebarOpen(false);
                  } else if (item.title === 'Bid History') {
                    navigate('/mybids');
                    setIsSidebarOpen(false);
                  } else if (item.title === 'Logout') {
                    handleLogout();
                  } else {
                    setIsSidebarOpen(false);
                  }
                }}
              >
                <div className="menu-icon-wrapper">
                  {item.icon}
                </div>
                <div className="menu-content">
                  <div className="menu-title">{item.title}</div>
                  <div className="menu-subtitle">{item.subtitle}</div>
                </div>
                <svg className="menu-arrow" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 4L13 10L7 16" stroke="#999" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            ))}
          </nav>
        </aside>
      )}

      {/* Main Content */}
      <div className={`main-content ${isAuthRoute ? 'auth-route' : ''} ${!isValidRoute(location.pathname) ? 'auth-route' : ''}`}>
        <Routes>
          <Route path="/login" element={<Login whatsappUrl={newsData.whatsapp_url} />} />
          <Route path="/register" element={<Register whatsappUrl={newsData.whatsapp_url} />} />
          <Route path="/" element={
            <>
              <div className="sticky-header">
                {/* Header */}
                <header className="header">
                  <button className="menu-btn" onClick={() => setIsSidebarOpen(true)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M3 12H21M3 6H21M3 18H21" stroke="white" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>

                  <div className="header-title"></div>

                  <div className="header-right">
                    <button className="wallet-btn" onClick={() => navigate("/funds")}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2" />
                        <path d="M2 10H22" stroke="white" strokeWidth="2" />
                        <circle cx="18" cy="15" r="1.5" fill="white" />
                      </svg>
                      <span className="wallet-balance">₹ {walletBalance}</span>
                    </button>
                    <button
                      className="notification-btn"
                      onClick={() => setShowNotifications(!showNotifications)}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {notifications.some(notif => notif.is_read === 0) && (
                        <span className="notification-badge">{notifications.filter(notif => notif.is_read === 0).length}</span>
                      )}
                    </button>
                  </div>
                </header>

                {/* News Headline Component */}
                <NewsHeadline />

                {/* Notifications Modal */}
                {showNotifications && (
                  <div className="notifications-modal">
                    <div className="notifications-header">
                      <h3>Notifications</h3>
                      <div className="notifications-header-actions">
                        {notifications.some(notif => notif.is_read === 0) && (
                          <button className="mark-read-btn" onClick={markNotificationsAsRead}>
                            Mark Read
                          </button>
                        )}
                        <button className="close-btn" onClick={() => setShowNotifications(false)}>×</button>
                      </div>
                    </div>
                    <div className="notifications-list">
                      {notifications.length > 0 ? (
                        notifications.map(notif => (
                          <div key={notif.id} className={`notification-item ${notif.is_read ? 'read' : 'unread'}`}>
                            <div className={`notification-dot ${notif.is_read ? 'read' : ''}`}></div>
                            <div className="notification-content">
                              {notif.title && <p className="notification-title">{notif.title}</p>}
                              <p className="notification-message">{notif.message}</p>
                              <span className="notification-time">{notif.time}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="notifications-empty">No notifications</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="action-buttons">
                  <div className="action-item">
                    <button className="action-icon addmoney" onClick={() => navigate('/addfunds')}>
                      <svg viewBox="0 0 24 24" width="32" height="32" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#feffff"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M21.1009 8.00353C21.0442 7.99996 20.9825 7.99998 20.9186 8L20.9026 8.00001H18.3941C16.3264 8.00001 14.5572 9.62757 14.5572 11.75C14.5572 13.8724 16.3264 15.5 18.3941 15.5H20.9026L20.9186 15.5C20.9825 15.5 21.0442 15.5001 21.1009 15.4965C21.9408 15.4434 22.6835 14.7862 22.746 13.8682C22.7501 13.808 22.75 13.7431 22.75 13.683L22.75 13.6667V9.83334L22.75 9.81702C22.75 9.75688 22.7501 9.69199 22.746 9.6318C22.6835 8.71381 21.9408 8.05657 21.1009 8.00353ZM18.1717 12.75C18.704 12.75 19.1355 12.3023 19.1355 11.75C19.1355 11.1977 18.704 10.75 18.1717 10.75C17.6394 10.75 17.2078 11.1977 17.2078 11.75C17.2078 12.3023 17.6394 12.75 18.1717 12.75Z" fill="#feffff"></path> <path fill-rule="evenodd" clip-rule="evenodd" d="M20.9179 17C21.067 16.9961 21.1799 17.1342 21.1394 17.2778C20.9387 17.9902 20.62 18.5975 20.1088 19.1088C19.3604 19.8571 18.4114 20.1892 17.239 20.3469C16.0998 20.5 14.6442 20.5 12.8064 20.5H10.6936C8.85583 20.5 7.40019 20.5 6.26098 20.3469C5.08856 20.1892 4.13961 19.8571 3.39124 19.1088C2.64288 18.3604 2.31076 17.4114 2.15314 16.239C1.99997 15.0998 1.99998 13.6442 2 11.8064V11.6936C1.99998 9.85583 1.99997 8.40019 2.15314 7.26098C2.31076 6.08856 2.64288 5.13961 3.39124 4.39124C4.13961 3.64288 5.08856 3.31076 6.26098 3.15314C7.40019 2.99997 8.85582 2.99998 10.6936 3L12.8064 3C14.6442 2.99998 16.0998 2.99997 17.239 3.15314C18.4114 3.31076 19.3604 3.64288 20.1088 4.39124C20.62 4.90252 20.9386 5.50974 21.1394 6.22218C21.1799 6.36575 21.067 6.50387 20.9179 6.5L18.394 6.50001C15.5574 6.50001 13.0571 8.74091 13.0571 11.75C13.0571 14.7591 15.5574 17 18.394 17L20.9179 17ZM5.75 7C5.33579 7 5 7.33579 5 7.75C5 8.16421 5.33579 8.5 5.75 8.5H9.75C10.1642 8.5 10.5 8.16421 10.5 7.75C10.5 7.33579 10.1642 7 9.75 7H5.75Z" fill="#feffff"></path> </g></svg>
                    </button>
                    <span>Add Money</span>
                  </div>
                  <div className="action-item">
                    <button className="action-icon" onClick={() => navigate('/withdrawfunds')}>
                      <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                        <path d="M16 8V24M16 24L12 20M16 24L20 20" stroke="white" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                    <span>Withdraw</span>
                  </div>
                  <div className="action-item">
                    <button className="action-icon telegram bid-history" onClick={() => window.open(newsData.telegram_url, '_blank')}>
                      <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="32" height="32" viewBox="0 0 48 48">
                        <path fill="#29b6f6" d="M24,4C13,4,4,13,4,24s9,20,20,20s20-9,20-20S35,4,24,4z"></path><path fill="#fff" d="M34,15l-3.7,19.1c0,0-0.2,0.9-1.2,0.9c-0.6,0-0.9-0.3-0.9-0.3L20,28l-4-2l-5.1-1.4c0,0-0.9-0.3-0.9-1	c0-0.6,0.9-0.9,0.9-0.9l21.3-8.5c0,0,0.7-0.2,1.1-0.2c0.3,0,0.6,0.1,0.6,0.5C34,14.8,34,15,34,15z"></path><path fill="#b0bec5" d="M23,30.5l-3.4,3.4c0,0-0.1,0.1-0.3,0.1c-0.1,0-0.1,0-0.2,0l1-6L23,30.5z"></path><path fill="#cfd8dc" d="M29.9,18.2c-0.2-0.2-0.5-0.3-0.7-0.1L16,26c0,0,2.1,5.9,2.4,6.9c0.3,1,0.6,1,0.6,1l1-6l9.8-9.1	C30,18.7,30.1,18.4,29.9,18.2z"></path>
                      </svg>
                    </button>
                    <span>Telegram</span>
                  </div>
                  <div className="action-item">
                    <button className="action-icon whatsapp" onClick={() => {
                      const waUrl = newsData.whatsapp_url || 'https://wa.me/918000673615';
                      if (waUrl) {
                        window.location.href = waUrl;
                      }
                    }}>
                      <svg width="32" height="32" viewBox="0 0 48 48" fill="white">
                        <path d="M4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5c5.1,0,9.8,2,13.4,5.6	C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19c0,0,0,0,0,0h0c-3.2,0-6.3-0.8-9.1-2.3L4.9,43.3z"></path>
                        <path d="M4.9,43.8c-0.1,0-0.3-0.1-0.4-0.1c-0.1-0.1-0.2-0.3-0.1-0.5L7,33.5c-1.6-2.9-2.5-6.2-2.5-9.6	C4.5,13.2,13.3,4.5,24,4.5c5.2,0,10.1,2,13.8,5.7c3.7,3.7,5.7,8.6,5.7,13.8c0,10.7-8.7,19.5-19.5,19.5c-3.2,0-6.3-0.8-9.1-2.3	L5,43.8C5,43.8,4.9,43.8,4.9,43.8z"></path>
                        <path d="M24,5c5.1,0,9.8,2,13.4,5.6C41,14.2,43,18.9,43,24c0,10.5-8.5,19-19,19h0c-3.2,0-6.3-0.8-9.1-2.3	L4.9,43.3l2.7-9.8C5.9,30.6,5,27.3,5,24C5,13.5,13.5,5,24,5 M24,43L24,43L24,43 M24,43L24,43L24,43 M24,4L24,4C13,4,4,13,4,24	c0,3.4,0.8,6.7,2.5,9.6L3.9,43c-0.1,0.3,0,0.7,0.3,1c0.2,0.2,0.4,0.3,0.7,0.3c0.1,0,0.2,0,0.3,0l9.7-2.5c2.8,1.5,6,2.2,9.2,2.2	c11,0,20-9,20-20c0-5.3-2.1-10.4-5.8-14.1C34.4,6.1,29.4,4,24,4L24,4z"></path>
                        <path d="M35.2,12.8c-3-3-6.9-4.6-11.2-4.6C15.3,8.2,8.2,15.3,8.2,24c0,3,0.8,5.9,2.4,8.4L11,33l-1.6,5.8	l6-1.6l0.6,0.3c2.4,1.4,5.2,2.2,8,2.2h0c8.7,0,15.8-7.1,15.8-15.8C39.8,19.8,38.2,15.8,35.2,12.8z"></path>
                        <path d="M19.3,16c-0.4-0.8-0.7-0.8-1.1-0.8c-0.3,0-0.6,0-0.9,0	s-0.8,0.1-1.3,0.6c-0.4,0.5-1.7,1.6-1.7,4s1.7,4.6,1.9,4.9s3.3,5.3,8.1,7.2c4,1.6,4.8,1.3,5.7,1.2c0.9-0.1,2.8-1.1,3.2-2.3	c0.4-1.1,0.4-2.1,0.3-2.3c-0.1-0.2-0.4-0.3-0.9-0.6s-2.8-1.4-3.2-1.5c-0.4-0.2-0.8-0.2-1.1,0.2c-0.3,0.5-1.2,1.5-1.5,1.9	c-0.3,0.3-0.6,0.4-1,0.1c-0.5-0.2-2-0.7-3.8-2.4c-1.4-1.3-2.4-2.8-2.6-3.3c-0.3-0.5,0-0.7,0.2-1c0.2-0.2,0.5-0.6,0.7-0.8	c0.2-0.3,0.3-0.5,0.5-0.8c0.2-0.3,0.1-0.6,0-0.8C20.6,19.3,19.7,17,19.3,16z" fill="#40c351"></path>
                      </svg>
                    </button>
                    <span>Whatsapp</span>
                  </div>
                </div>

                {/* Quick Access Buttons */}
                {/* <div className="quick-access">
                  <button className="quick-btn quick-btn-full" onClick={() => navigate('/starline')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2L15 9L22 10L17 15L18 22L12 18L6 22L7 15L2 10L9 9L12 2Z" stroke="#d4af37" strokeWidth="2" />
                    </svg>
                    Starline
                  </button>
                </div> */}
              </div>

              {/* Game Cards */}
              <div className="gcard-container">
                {loading ? (
                  <div className="gcard-loading">
                    <div className="loading-spinner-home"></div>
                    <p>Loading games...</p>
                  </div>
                ) : error ? (
                  <div className="gcard-error">
                    <p>Error loading games: {error}</p>
                  </div>
                ) : games.length > 0 ? (
                  games.map((game) => (
                    <div key={game.id} className="gcard-item" onClick={(e) => handlePlayClick(game, e)}>
                      {/* Left: text info */}
                      <div className="gcard-info">
                        <h3 className="gcard-title">{game.name}</h3>
                        <div className="gcard-numbers">{game.numbers}</div>
                        <div className={`gcard-status ${getMarketStatus(game.rawOpen, game.rawClose, game.gameClosed) === 'running'
                          ? 'status-running'
                          : getMarketStatus(game.rawOpen, game.rawClose, game.gameClosed) === 'yet-to-open'
                            ? 'status-yet-to-open'
                            : 'status-closed'
                          }`}>
                          {getMarketStatusText(game.rawOpen, game.rawClose, game.gameClosed)}
                        </div>
                        <div className="gcard-timing">
                          <span className="gcard-timing-value">
                            Open: <span className="gcard-time-red">{game.openTime}</span>
                          </span>
                          <span className="gcard-timing-value">
                            Close: <span className="gcard-time-red">{game.closeTime}</span>
                          </span>
                        </div>
                      </div>

                      {/* Right: calendar + play buttons stacked */}
                      <div className="gcard-actions">
                        <button
                          className="gcard-calendar"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/result-chart/${game.id}`, { state: { gameName: game.name } });
                          }}
                        >
                          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M21 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V3M15 10V17M7 13V17M19 5V17M11 7V17" stroke="#ffffff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path> </g></svg>
                        </button>
                        <button
                          className={`gcard-play ${isMarketOpen(game.rawOpen, game.rawClose, game.gameClosed) ? 'market-open' : 'market-closed'}`}
                          onClick={(e) => handlePlayClick(game, e)}
                        >
                          {isMarketOpen(game.rawOpen, game.rawClose, game.gameClosed) ? (
                            <svg viewBox="0 0 24 24" id="_24x24_On_Light_Play" data-name="24x24/On Light/Play" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <rect id="view-box" width="24" height="24" fill="none"></rect> <path id="Shape" d="M0,9.75A9.75,9.75,0,1,1,9.75,19.5,9.761,9.761,0,0,1,0,9.75Zm1.5,0A8.25,8.25,0,1,0,9.75,1.5,8.259,8.259,0,0,0,1.5,9.75Zm5.75-3.8,7,3.8-7,3.8Z" transform="translate(2.25 2.25)" fill="#ffffff"></path> </g></svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" />
                              <line x1="5" y1="5" x2="19" y2="19" stroke="white" strokeWidth="2" strokeLinecap="round" />
                              <line x1="19" y1="5" x2="5" y2="19" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="gcard-empty">
                    <p>No games available at the moment</p>
                  </div>
                )}
              </div>
            </>
          } />
          <Route path="/starline" element={<Starline onBack={() => navigate('/')} />} />
          <Route path="/galidesawar" element={<GaliDesawar onBack={() => navigate('/')} />} />
          <Route path="/starline-game-selection/:gameId" element={
            <StarlineGameSelection
              gameName={location.state?.gameName}
              onBack={() => navigate('/starline')}
              gameId={location.pathname.split('/')[2]}
            />
          } />
          <Route path="/game-selection/:gameId" element={
            <GameSelection
              gameName={location.state?.gameName}
              onBack={() => navigate('/')}
              gameType="normal"
              gameId={location.pathname.split('/')[2]}
            />
          } />
          <Route path="/single-digit-game/:gameId" element={
            <SingleDigitGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameId: gameId,
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/jodi-game/:gameId" element={
            <JodiGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/single-panna-game/:gameId" element={
            <SinglePannaGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/double-panna-game/:gameId" element={
            <DoublePannaGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/triple-panna-game/:gameId" element={
            <TriplePannaGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/half-sangam-game/:gameId" element={
            <HalfSangamGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/full-sangam-game/:gameId" element={
            <FullSangamGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/starline-single-digit-game/:gameId" element={
            <StarlineSingleDigitGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/starline-game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/starline-single-panna-game/:gameId" element={
            <StarlineSinglePannaGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/starline-game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/starline-double-panna-game/:gameId" element={
            <StarlineDoublePannaGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/starline-game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/starline-triple-panna-game/:gameId" element={
            <StarlineTriplePannaGame
              gameName={location.state?.gameName}
              onBack={() => {
                const gameId = location.pathname.split('/')[2];
                navigate(`/starline-game-selection/${gameId}`, {
                  state: {
                    gameName: location.state?.gameName,
                    openTime: location.state?.openTime,
                    closeTime: location.state?.closeTime
                  }
                });
              }}
            />
          } />
          <Route path="/mybids" element={<MyBids onBack={() => navigate('/')} />} />
          <Route path="/normal-bid-history" element={<NormalBidHistory onBack={() => navigate('/mybids')} />} />
          <Route path="/starline-bid-history" element={<StarlineBidHistory onBack={() => navigate('/mybids')} />} />
          <Route path="/passbook" element={<Passbook onBack={() => navigate('/')} />} />
          <Route path="/funds" element={
            <Funds
              onBack={() => navigate('/')}
              onNavigateToAddFunds={() => navigate('/addfunds')}
              onNavigateToWithdrawFunds={() => navigate('/withdrawfunds')}
              onNavigateToBankDetails={() => navigate('/bankdetails')}
              onNavigateToDepositHistory={() => navigate('/deposit-history')}
              onNavigateToWithdrawalHistory={() => navigate('/withdrawal-history')}
            />
          } />
          <Route path="/addfunds" element={<AddFunds onBack={() => navigate('/funds')} />} />
          <Route path="/withdrawfunds" element={<WithdrawFunds onBack={() => navigate('/funds')} />} />
          <Route path="/withdrawal-history" element={<WithdrawalHistory onBack={() => navigate('/funds')} />} />
          <Route path="/deposit-history" element={<DepositHistory onBack={() => navigate('/funds')} />} />
          <Route path="/contactus" element={<ContactUs onBack={() => navigate('/')} contactNumber={newsData.contact_number} whatsappUrl={newsData.whatsapp_url} />} />
          <Route path="/bankdetails" element={<BankDetails onBack={() => navigate('/funds')} />} />
          <Route path="/gamerates" element={<GameRates onBack={() => navigate('/')} onOpenSidebar={() => setIsSidebarOpen(true)} />} />
          <Route path="/result-chart/:gameId" element={<ResultChart />} />
          {/* 404 Not Found Route - must be last */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Bottom Navigation - Hide on game selection screens, result chart, auth pages, and 404 */}
        {!location.pathname.startsWith('/game-selection') &&
          !location.pathname.startsWith('/starline-game-selection') &&
          !location.pathname.startsWith('/single-digit-game') &&
          !location.pathname.startsWith('/jodi-game') &&
          !location.pathname.startsWith('/single-panna-game') &&
          !location.pathname.startsWith('/double-panna-game') &&
          !location.pathname.startsWith('/triple-panna-game') &&
          !location.pathname.startsWith('/half-sangam-game') &&
          !location.pathname.startsWith('/full-sangam-game') &&
          !location.pathname.startsWith('/starline-single-digit-game') &&
          !location.pathname.startsWith('/starline-single-panna-game') &&
          !location.pathname.startsWith('/starline-double-panna-game') &&
          !location.pathname.startsWith('/starline-triple-panna-game') &&
          !location.pathname.startsWith('/result-chart') &&
          !isAuthRoute &&
          isValidRoute(location.pathname) && (
            <nav className="bottom-nav">
              <button className={`nav-item ${location.pathname === '/mybids' ? 'active' : ''}`} onClick={() => navigate('/mybids')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 7L12 3L4 7M20 7V17L12 21M20 7L12 11M12 21L4 17V7M12 21V11M4 7L12 11" stroke={location.pathname === '/mybids' ? '#d4af37' : '#d4af37'} strokeWidth="2" />
                </svg>
                <span style={{ color: location.pathname === '/mybids' ? '#d4af37' : '#d4af37', fontWeight: location.pathname === '/mybids' ? '700' : '500' }}>My Bids</span>
              </button>
              <button className={`nav-item ${location.pathname === '/passbook' ? 'active' : ''}`} onClick={() => navigate('/passbook')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="4" y="4" width="16" height="16" rx="2" stroke="#d4af37" strokeWidth="2" />
                  <path d="M4 10H20M8 14H16" stroke="#d4af37" strokeWidth="2" />
                </svg>
                <span style={{ color: location.pathname === '/passbook' ? '#d4af37' : '#d4af37', fontWeight: location.pathname === '/passbook' ? '700' : '500' }}>PassBook</span>
              </button>
              <button className={`nav-item nav-home ${location.pathname === '/' ? 'active' : ''}`} onClick={() => navigate('/')}>
                <svg width="32" height="32" viewBox="0 0 32 32" fill="white">
                  <path d="M16 6L6 14V26H12V20H20V26H26V14L16 6Z" />
                </svg>
              </button>
              <button className={`nav-item ${location.pathname === '/funds' ? 'active' : ''}`} onClick={() => navigate('/funds')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect x="2" y="6" width="20" height="14" rx="2" stroke={location.pathname === '/funds' ? '#d4af37' : '#d4af37'} strokeWidth="2" />
                  <path d="M2 10H22" stroke={location.pathname === '/funds' ? '#d4af37' : '#d4af37'} strokeWidth="2" />
                </svg>
                <span style={{ color: location.pathname === '/funds' ? '#d4af37' : '#d4af37', fontWeight: location.pathname === '/funds' ? '700' : '500' }}>Funds</span>
              </button>
              <button className={`nav-item ${location.pathname === '/contactus' ? 'active' : ''}`} onClick={() => navigate('/contactus')}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke={location.pathname === '/contactus' ? '#d4af37' : '#d4af37'} strokeWidth="2" />
                  <path d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14M9 9H9.01M15 9H15.01" stroke={location.pathname === '/contactus' ? '#d4af37' : '#d4af37'} strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span style={{ color: location.pathname === '/contactus' ? '#d4af37' : '#d4af37', fontWeight: location.pathname === '/contactus' ? '700' : '500' }}>Chat</span>
              </button>
            </nav>
          )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="toast-notification">
          Market Closed
        </div>
      )}
    </div>
  );
}

// Main App component that wraps AppContent with providers
function App() {
  return (
    <WalletProvider>
      <ToastProvider>
        <Toaster position="top-center" />
        <Toast />
        <AppContent />
      </ToastProvider>
    </WalletProvider>
  );
}

// Wrapper component to maintain compatibility
function AppWrapper() {
  return <App />;
}

export default AppWrapper;
